import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBrain, FiTrendingUp, FiAlertTriangle, FiCheckCircle, FiTarget, FiDollarSign } = FiIcons;

function AIInsights({ userProfile, goals, totalRequiredSIP, monthlySurplus }) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  const generatePersonalizedInsights = () => {
    const insights = [];
    const expenseRatio = userProfile.monthlyExpenses / userProfile.monthlyIncome;
    const shortfall = totalRequiredSIP - monthlySurplus;
    const incomeGrowth = userProfile.incomeGrowthRate || 8;
    const expenseGrowth = userProfile.expenseGrowthRate || 6;

    // Income vs Expense Growth Analysis
    if (incomeGrowth > expenseGrowth) {
      const surplusGrowthRate = incomeGrowth - expenseGrowth;
      insights.push({
        type: 'success',
        icon: FiTrendingUp,
        title: 'Growing Surplus Advantage',
        description: `Your income grows ${incomeGrowth}% vs expenses at ${expenseGrowth}%. This creates a ${surplusGrowthRate}% annual surplus growth.`,
        suggestions: [
          `In 5 years, your surplus could grow to ₹${((monthlySurplus * Math.pow(1 + surplusGrowthRate/100, 5))).toLocaleString()}`,
          'Consider increasing SIP amounts annually as surplus grows',
          'This trend helps achieve goals faster than current projections'
        ]
      });
    }

    // Goal-Specific Analysis
    const educationGoals = goals.filter(goal => 
      goal.name.toLowerCase().includes('education') || 
      goal.name.toLowerCase().includes('child')
    );
    
    if (educationGoals.length > 0) {
      const totalEducationCost = educationGoals.reduce((sum, goal) => sum + goal.goalAmount, 0);
      insights.push({
        type: 'warning',
        icon: FiTarget,
        title: 'Education Planning Priority',
        description: `You have ${educationGoals.length} education goal(s) worth ₹${totalEducationCost.toLocaleString()}.`,
        suggestions: [
          'Education costs inflate at 8-10% annually - start early',
          'Consider ELSS funds for tax benefits on education savings',
          'PPF and SSY (for girl child) offer tax-free returns'
        ]
      });
    }

    const houseGoals = goals.filter(goal => 
      goal.name.toLowerCase().includes('house') || 
      goal.name.toLowerCase().includes('home')
    );
    
    if (houseGoals.length > 0) {
      insights.push({
        type: 'opportunity',
        icon: FiDollarSign,
        title: 'Property Investment Strategy',
        description: 'House purchase detected in your goals.',
        suggestions: [
          'Consider home loan tax benefits (₹2L under 24B + ₹1.5L under 80C)',
          'Real estate typically appreciates 6-8% annually',
          'Factor in registration, stamp duty (6-8% of property value)'
        ]
      });
    }

    // Asset Allocation Insights
    const totalAssets = Object.values(userProfile.existingInvestments || {}).reduce((sum, val) => sum + val, 0);
    const equityAssets = (userProfile.existingInvestments?.mutualFunds || 0) + 
                        (userProfile.existingInvestments?.stocks || 0);
    const debtAssets = (userProfile.existingInvestments?.fixedDeposits || 0) + 
                      (userProfile.existingInvestments?.epfPpf || 0);
    
    if (totalAssets > 0) {
      const equityRatio = (equityAssets / totalAssets) * 100;
      const age = userProfile.currentAge;
      const suggestedEquityRatio = Math.max(30, 100 - age);
      
      if (equityRatio < suggestedEquityRatio - 20) {
        insights.push({
          type: 'opportunity',
          icon: FiTrendingUp,
          title: 'Asset Allocation Optimization',
          description: `Your equity allocation is ${equityRatio.toFixed(0)}%. For age ${age}, consider ${suggestedEquityRatio}%.`,
          suggestions: [
            `Gradually shift ₹${((suggestedEquityRatio - equityRatio) * totalAssets / 100).toLocaleString()} to equity`,
            'Use SIP to build equity allocation over time',
            'Consider balanced/hybrid funds for gradual transition'
          ]
        });
      }
    }

    // Loan Insights
    const goalsWithLoans = goals.filter(goal => goal.loanOption && goal.loanEMI > 0);
    if (goalsWithLoans.length > 0) {
      const totalLoanEMI = goalsWithLoans.reduce((sum, goal) => sum + goal.loanEMI, 0);
      const totalLoanInterest = goalsWithLoans.reduce((sum, goal) => 
        sum + (goal.totalLoanCost - goal.inflatedLoanAmount), 0
      );
      
      insights.push({
        type: 'warning',
        icon: FiAlertTriangle,
        title: 'Loan Strategy Analysis',
        description: `${goalsWithLoans.length} goal(s) with loans. Total interest: ₹${totalLoanInterest.toLocaleString()}`,
        suggestions: [
          'Consider prepayments to reduce interest burden',
          `EMI of ₹${totalLoanEMI.toLocaleString()} will adjust with inflation`,
          'Compare loan rates with investment returns for optimization'
        ]
      });
    }

    // Emergency Fund Check
    const liquidAssets = (userProfile.assets?.bankSavings || 0) + 
                        (userProfile.existingInvestments?.fixedDeposits || 0);
    const requiredEmergencyFund = userProfile.monthlyExpenses * 6;
    
    if (liquidAssets < requiredEmergencyFund) {
      insights.push({
        type: 'critical',
        icon: FiAlertTriangle,
        title: 'Emergency Fund Gap',
        description: `Emergency fund: ₹${liquidAssets.toLocaleString()} vs required ₹${requiredEmergencyFund.toLocaleString()}`,
        suggestions: [
          `Build additional ₹${(requiredEmergencyFund - liquidAssets).toLocaleString()} emergency corpus`,
          'Use liquid funds or high-yield savings for emergency fund',
          'Target 6 months expenses before other investments'
        ]
      });
    }

    // Tax Optimization
    const highIncomeThreshold = 1000000; // 10L annual income
    if (userProfile.monthlyIncome * 12 > highIncomeThreshold) {
      insights.push({
        type: 'opportunity',
        icon: FiDollarSign,
        title: 'Tax Optimization Opportunity',
        description: 'High income bracket - maximize tax-saving investments.',
        suggestions: [
          'ELSS funds: ₹1.5L limit under 80C with growth potential',
          'PPF: ₹1.5L limit, 15-year lock-in, tax-free returns',
          'NPS: Additional ₹50K deduction under 80CCD(1B)'
        ]
      });
    }

    return insights;
  };

  const insights = generatePersonalizedInsights();

  const getIconColor = (type) => {
    switch (type) {
      case 'critical': return 'text-red-500';
      case 'warning': return 'text-yellow-500';
      case 'opportunity': return 'text-green-500';
      case 'success': return 'text-blue-500';
      default: return 'text-gray-500';
    }
  };

  const getBgColor = (type) => {
    switch (type) {
      case 'critical': return 'bg-red-50 border-red-200';
      case 'warning': return 'bg-yellow-50 border-yellow-200';
      case 'opportunity': return 'bg-green-50 border-green-200';
      case 'success': return 'bg-blue-50 border-blue-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
          <SafeIcon icon={FiBrain} className="text-purple-600 text-lg" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">AI-Powered Financial Insights</h3>
      </div>

      <div className="space-y-6">
        {insights.map((insight, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`rounded-lg border p-4 ${getBgColor(insight.type)}`}
          >
            <div className="flex items-start space-x-3">
              <SafeIcon
                icon={insight.icon}
                className={`text-lg mt-1 ${getIconColor(insight.type)}`}
              />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">{insight.title}</h4>
                <p className="text-sm text-gray-700 mb-3">{insight.description}</p>
                
                {insight.suggestions && (
                  <ul className="space-y-1">
                    {insight.suggestions.map((suggestion, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start space-x-2">
                        <span className="text-gray-400 mt-1">•</span>
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </motion.div>
        ))}

        {insights.length === 0 && (
          <div className="text-center py-8">
            <SafeIcon icon={FiCheckCircle} className="text-green-500 text-4xl mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Excellent Financial Planning!</h4>
            <p className="text-gray-600">Your financial strategy is well-optimized based on your current data.</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default AIInsights;